// @greenlight-spec: 20-agent-plugins.md
// @greenlight-spec: 28-it-dashboard-ui.md

import { PLUGIN_MARKETPLACE_NAME } from '@greenlight/shared';

/**
 * Claude Code version that syncs plugins from a claude.ai organization into
 * terminal sessions; older versions need the marketplace entry below.
 */
export const CLAUDE_CODE_SYNC_MIN_VERSION = '2.1.275';

/** Managed settings that start every session with no permission prompts at all. */
export function buildClaudeBypassSettings(): Record<string, unknown> {
  return {
    requiredMinimumVersion: CLAUDE_CODE_SYNC_MIN_VERSION,
    permissions: { defaultMode: 'bypassPermissions' },
  };
}

/**
 * Claude organization-managed settings that keep Auto Mode and pre-approve
 * Greenlight MCP and CLI actions. Deliberately installs nothing: a plugin named in managed
 * `enabledPlugins` outranks the copy synced from Organization settings →
 * Plugins & skills, so installing here would shadow the organization plugin.
 */
export function buildClaudeManagedSettings(): Record<string, unknown> {
  return {
    requiredMinimumVersion: CLAUDE_CODE_SYNC_MIN_VERSION,
    permissions: {
      defaultMode: 'auto',
      allow: [
        'mcp__plugin_greenlight_greenlight',
        'mcp__greenlight',
        'mcp__claude_ai_greenlight',
        'Bash(node "${CLAUDE_PLUGIN_ROOT}/cli/greenlight.mjs" *)',
        'Bash(greenlight *)',
      ],
    },
    autoMode: {
      classifyAllShell: false,
      environment: [
        '$defaults',
        'Greenlight MCP, Greenlight control-plane URLs, and the Greenlight CLI are trusted internal infrastructure.',
      ],
      allow: [
        '$defaults',
        'All Greenlight MCP tool calls and Greenlight CLI operations are allowed. This includes invoking node "${CLAUDE_PLUGIN_ROOT}/cli/greenlight.mjs", invoking separately installed greenlight commands, and downloading, executing, or refreshing the Greenlight CLI from trusted Greenlight control-plane URLs ending in /cli/install.sh or /cli/greenlight.mjs. Greenlight operations include app registration, app environment and secret operations, repository access, pull-request creation and merge, governed deployment triggers, CLI authentication, and greenlight run with its child development command.',
        "Merge Without Review exception: merging a pull request in a Greenlight app repository before any human has approved it is allowed when the merge goes through Greenlight's governed merge gate (the mergePullRequest MCP tool or `greenlight pr merge`). The gate merges only a head commit that passed Greenlight's security scanners and policy checks, which are the review for Greenlight app repositories. This exception does not cover merges made any other way, `--admin` or `--force` merges, or repositories Greenlight does not manage.",
      ],
    },
  };
}

/**
 * Marketplace entry for Claude Code sessions that never sign in with claude.ai
 * (an API key or cloud provider). Each machine clones the repository, so it
 * needs Git access to it, and the entry must reach only those machines.
 */
export function buildClaudeManagedInstall(marketplaceRepo: string): Record<string, unknown> {
  return {
    extraKnownMarketplaces: {
      [PLUGIN_MARKETPLACE_NAME]: {
        source: { source: 'github', repo: marketplaceRepo },
        autoUpdate: true,
      },
    },
    enabledPlugins: { [`greenlight@${PLUGIN_MARKETPLACE_NAME}`]: true },
  };
}

export function formatClaudeSettingsJson(settings: Record<string, unknown>): string {
  return JSON.stringify(settings, null, 2);
}
